<template>
  <main class="archive-page">
    <header class="archive-header">
      <div>
        <p class="archive-kicker">Archive</p>
        <h1>文章归档</h1>
        <p class="archive-desc">按分类与年份浏览文章，只加载标题、日期和标签。</p>
      </div>
      <div class="archive-stats">
        <span><strong>{{ totalPosts }}</strong> posts</span>
        <span><strong>{{ totalYears }}</strong> years</span>
      </div>
    </header>

    <div class="archive-shell">
      <aside class="archive-sidebar">
        <label class="archive-search">
          <span>Search</span>
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="标题或标签"
            autocomplete="off"
          >
        </label>

        <div class="filter-group">
          <button
            v-for="item in categoryFilters"
            :key="item.key"
            :class="{ active: activeCategory === item.key }"
            @click="activeCategory = item.key"
          >
            <span>{{ item.label }}</span>
            <em>{{ item.count }}</em>
          </button>
        </div>

        <nav class="year-nav" v-if="visibleYears.length">
          <a
            v-for="group in visibleYears"
            :key="group.year"
            :href="'#archive-' + group.year"
          >
            {{ group.year }}
            <span>{{ group.posts.length }}</span>
          </a>
        </nav>
      </aside>

      <section class="archive-main">
        <div v-if="loading" class="archive-state">加载中...</div>
        <div v-else-if="error" class="archive-state error">{{ error }}</div>
        <div v-else-if="!visibleYears.length" class="archive-state">没有匹配的文章</div>

        <section
          v-for="group in visibleYears"
          :id="'archive-' + group.year"
          :key="group.year"
          class="year-section"
        >
          <div class="year-heading">
            <h2>{{ group.year }}</h2>
            <span>{{ group.posts.length }} 篇</span>
          </div>

          <article
            v-for="blogpost in group.posts"
            :key="blogpost.pk"
            class="archive-item"
          >
            <time :datetime="blogpost.fields.pub_date">
              <span>{{ formatDate(blogpost.fields.pub_date, '%b') }}</span>
              <strong>{{ formatDate(blogpost.fields.pub_date, '%d') }}</strong>
            </time>

            <div class="archive-item-body">
              <router-link
                class="archive-title"
                :to="{ name: 'postDetail', params: { url: blogpost.url } }"
              >
                {{ blogpost.fields.title }}
              </router-link>
              <div class="archive-meta">
                <span>{{ categoryLabel(blogpost.fields.category || blogpost._category) }}</span>
                <router-link
                  v-for="tag in blogpost.tags"
                  :key="tag.pk"
                  :to="{ name: 'tagContent', params: { tag: tag.fields.name } }"
                >
                  {{ tag.fields.name }}
                </router-link>
              </div>
            </div>
          </article>
        </section>
      </section>
    </div>
  </main>
</template>

<script>
const CATEGORY_LABELS = {
  reading: 'Reading',
  finance: 'Finance',
  su: 'Summary',
  tech: 'Tech',
  startup: 'Start Up',
  ml: 'Machine Learning',
  oth: 'Others'
}

export default {
  name: 'archive',
  data () {
    return {
      archiveData: [],
      activeCategory: 'all',
      keyword: '',
      loading: false,
      error: ''
    }
  },
  computed: {
    categoryFilters () {
      const items = this.archiveData.map(([key, years]) => ({
        key,
        label: this.categoryLabel(key),
        count: years.reduce((sum, [, posts]) => sum + posts.length, 0)
      })).filter(item => item.count > 0)
      const total = items.reduce((sum, item) => sum + item.count, 0)
      return [{ key: 'all', label: 'All', count: total }].concat(items)
    },
    totalPosts () {
      return this.categoryFilters.length ? this.categoryFilters[0].count : 0
    },
    totalYears () {
      return new Set(this.allPosts.map(post => new Date(post.fields.pub_date).getFullYear())).size
    },
    allPosts () {
      return this.archiveData.reduce((posts, [category, years]) => {
        years.forEach(([, yearPosts]) => {
          yearPosts.forEach(post => posts.push(Object.assign({ _category: category }, post)))
        })
        return posts
      }, [])
    },
    visibleYears () {
      const query = this.keyword.toLowerCase()
      const posts = this.allPosts.filter(post => {
        if (this.activeCategory !== 'all' && post._category !== this.activeCategory) {
          return false
        }
        if (!query) return true
        const title = (post.fields.title || '').toLowerCase()
        const tags = (post.tags || []).map(tag => tag.fields.name.toLowerCase()).join(' ')
        return title.indexOf(query) !== -1 || tags.indexOf(query) !== -1
      })
      const byYear = posts.reduce((groups, post) => {
        const year = new Date(post.fields.pub_date).getFullYear()
        if (!groups[year]) groups[year] = []
        groups[year].push(post)
        return groups
      }, {})
      return Object.keys(byYear)
        .sort((a, b) => Number(b) - Number(a))
        .map(year => ({
          year,
          posts: byYear[year].sort((a, b) => new Date(b.fields.pub_date) - new Date(a.fields.pub_date))
        }))
    }
  },
  methods: {
    categoryLabel (key) {
      return CATEGORY_LABELS[key] || 'Others'
    },
    formatDate (value, pattern) {
      if (this.$options.filters && this.$options.filters.date) {
        return this.$options.filters.date(value, pattern)
      }
      const date = new Date(value)
      if (pattern === '%d') return String(date.getDate()).padStart(2, '0')
      return date.toLocaleString('en', { month: 'short' })
    },
    async blogArchive () {
      const archiveCache = this.$store.state.archive
      if (archiveCache != null) {
        this.archiveData = archiveCache.data || []
        return
      }
      this.loading = true
      this.error = ''
      const reqUrl = this.$store.state.baseUrl + '/blog/api/archive'
      try {
        const response = await this.$http.get(reqUrl)
        const res = JSON.parse(response.bodyText)
        this.$store.commit('assignArchive', res)
        this.archiveData = res.data || []
      } catch (e) {
        this.error = '归档加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.blogArchive()
  }
}
</script>

<style scoped>
.archive-page {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 56px 0 80px;
  color: #1f2933;
}

.archive-header {
  display: flex;
  justify-content: space-between;
  gap: 32px;
  align-items: flex-end;
  padding-bottom: 28px;
  border-bottom: 1px solid #dbe3ea;
}

.archive-kicker {
  margin: 0 0 8px;
  color: #52616f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.archive-header h1 {
  margin: 0;
  font-size: 40px;
  line-height: 1.12;
  color: #111827;
}

.archive-desc {
  margin: 12px 0 0;
  color: #667085;
  font-size: 15px;
}

.archive-stats {
  display: flex;
  gap: 12px;
  color: #667085;
  white-space: nowrap;
}

.archive-stats span {
  border: 1px solid #dbe3ea;
  border-radius: 8px;
  padding: 10px 14px;
  background: #fff;
}

.archive-stats strong {
  color: #111827;
}

.archive-shell {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 44px;
  padding-top: 32px;
}

.archive-sidebar {
  position: sticky;
  top: 24px;
  align-self: start;
}

.archive-search span {
  display: block;
  margin-bottom: 8px;
  color: #52616f;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.archive-search input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  padding: 11px 12px;
  font-size: 15px;
  color: #111827;
  background: #fff;
}

.filter-group {
  display: grid;
  gap: 8px;
  margin-top: 22px;
}

.filter-group button,
.year-nav a {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 10px 12px;
  background: transparent;
  color: #344054;
  font-size: 14px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.filter-group button.active {
  border-color: #1f6feb;
  background: #eef5ff;
  color: #174ea6;
}

.filter-group em,
.year-nav span {
  color: #8a96a3;
  font-style: normal;
  font-size: 12px;
}

.year-nav {
  display: grid;
  gap: 4px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid #e5eaf0;
}

.year-nav a:hover {
  background: #f4f7fa;
}

.archive-main {
  min-width: 0;
}

.archive-state {
  padding: 48px 0;
  color: #667085;
  text-align: center;
}

.archive-state.error {
  color: #b42318;
}

.year-section {
  padding-bottom: 30px;
}

.year-heading {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 14px;
}

.year-heading h2 {
  margin: 0;
  color: #111827;
  font-size: 30px;
  line-height: 1;
}

.year-heading span {
  color: #8a96a3;
  font-size: 14px;
}

.archive-item {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 18px;
  padding: 18px 0;
  border-top: 1px solid #e5eaf0;
}

.archive-item time {
  display: grid;
  place-items: center;
  align-content: center;
  min-height: 58px;
  border: 1px solid #dbe3ea;
  border-radius: 8px;
  color: #52616f;
  background: #fff;
  font-size: 12px;
  text-transform: uppercase;
}

.archive-item time strong {
  color: #111827;
  font-size: 22px;
  line-height: 1.1;
}

.archive-title {
  display: inline-block;
  color: #111827;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.35;
  text-decoration: none;
}

.archive-title:hover {
  color: #1f6feb;
}

.archive-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  color: #667085;
  font-size: 13px;
}

.archive-meta span,
.archive-meta a {
  color: #667085;
  text-decoration: none;
}

.archive-meta a::before {
  content: "#";
}

.archive-meta a:hover {
  color: #1f6feb;
}

@media (max-width: 820px) {
  .archive-page {
    width: min(100% - 28px, 680px);
    padding-top: 32px;
  }

  .archive-header {
    display: block;
  }

  .archive-header h1 {
    font-size: 32px;
  }

  .archive-stats {
    margin-top: 20px;
  }

  .archive-shell {
    display: block;
    padding-top: 24px;
  }

  .archive-sidebar {
    position: static;
    margin-bottom: 28px;
  }

  .filter-group {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .year-nav {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 520px) {
  .archive-item {
    grid-template-columns: 48px minmax(0, 1fr);
    gap: 12px;
  }

  .archive-title {
    font-size: 17px;
  }

  .filter-group,
  .year-nav {
    grid-template-columns: 1fr;
  }
}
</style>
